import React, { useState } from 'react';
import DOMPurify from 'dompurify';

const GuideViewer = ({ guide, item, onClose }) => {
  const [activeTab, setActiveTab] = useState('guide');
  const [selectedVersion, setSelectedVersion] = useState(null);

  if (!guide) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="bg-black text-white px-6 py-4 flex justify-between items-center">
            <h2 className="text-xl font-bold">Equipment Guide: {item?.name}</h2>
            <button onClick={onClose} className="text-white hover:text-gray-300">✕</button>
          </div>
          <div className="p-6">
            <div className="text-center py-12">
              <h3 className="text-xl font-medium text-gray-900 mb-2">Guide Coming Soon</h3>
              <p className="text-gray-600 mb-4">
                A comprehensive guide for this equipment is being prepared by our lab staff.
              </p>
              <p className="text-sm text-gray-500">
                Contact lab staff for immediate assistance or check back soon.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderContent = (content) => {
    if (!content) return null;

    // Sanitize HTML content before rendering to prevent XSS attacks
    const sanitizedContent = DOMPurify.sanitize(content, {
      ALLOWED_TAGS: ['p', 'b', 'i', 'em', 'strong', 'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
                     'img', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'br', 'div', 'span', 'blockquote', 'pre', 'code'],
      ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'style', 'class', 'target', 'rel', 'border'],
      ALLOWED_STYLES: {
        '*': {
          'color': [/.*/],
          'text-align': [/.*/],
          'font-size': [/.*/],
          'font-weight': [/.*/],
          'padding': [/.*/],
          'margin': [/.*/],
          'background-color': [/.*/],
          'border-collapse': [/.*/],
          'width': [/.*/],
          'height': [/.*/],
          'max-width': [/.*/],
          'max-height': [/.*/]
        }
      },
      ALLOW_DATA_ATTR: false
    });

    return (
      <div
        className="prose prose-lg max-w-none"
        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
        style={{
          lineHeight: '1.6'
        }}
      />
    );
  };

  const renderImage = (image, index) => (
    <div key={index} className="mb-4">
      <img
        src={image.url}
        alt={image.altText}
        className="w-full max-w-md mx-auto rounded-lg shadow-md"
      />
      {image.caption && (
        <p className="text-sm text-gray-600 text-center mt-2 italic">
          {image.caption}
        </p>
      )}
    </div>
  );

  const getHazardIcon = (hazard) => {
    const icons = {
      'Electrical Shock': '⚡',
      'High Voltage': '⚡',
      'Moving Parts': '⚙️',
      'Hot Surfaces': '🔥',
      'Chemical Exposure': '🧪',
      'Radiation': '☢️',
      'Laser Light': '🔴',
      'Sharp Objects': '🔪',
      'Heavy Equipment': '🏗️',
      'Noise': '🔊',
      'UV Light': '☀️',
      'Compressed Gas': '💨'
    };
    return icons[hazard] || '⚠️';
  };

  const getPPEIcon = (ppe) => {
    const icons = {
      'Safety Glasses': '👓',
      'Lab Coat': '🥼',
      'Gloves': '🧤',
      'Face Shield': '🛡️',
      'Hearing Protection': '🎧',
      'Respirator': '😷',
      'Steel Toe Boots': '👢',
      'Hard Hat': '⛑️'
    };
    return icons[ppe] || '🛡️';
  };

  const reportProblem = () => {
    const subject = encodeURIComponent(`Problem Report: ${item.name}`);
    const body = encodeURIComponent(`Equipment: ${item.name}\nItem ID: ${item.id}\n\nPlease describe the problem:`);
    window.open(`mailto:lab-staff@ucf.edu?subject=${subject}&body=${body}`);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-black text-white px-6 py-4">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <h2 className="text-xl font-bold">{item?.name}</h2>
              <div className="flex items-center space-x-4 mt-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  item?.status === 'Available' 
                    ? 'bg-green-500 text-white' 
                    : 'bg-red-500 text-white'
                }`}>
                  {item?.status}
                </span>
                {guide.version && (
                  <span className="text-sm text-gray-300">
                    Guide v{guide.version}
                  </span>
                )}
              </div>
            </div>
            <button onClick={onClose} className="text-white hover:text-gray-300 ml-4">✕</button>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Hero Image & Quick Facts */}
            <div className="lg:col-span-1">
              {/* Hero Image */}
              {guide.images && guide.images.length > 0 && (
                <div className="mb-6">
                  <img
                    src={guide.images[0].url}
                    alt={guide.images[0].altText || item?.name}
                    className="w-full rounded-lg shadow-md"
                  />
                </div>
              )}
            </div>

            {/* Right Column - Content Tabs */}
            <div className="lg:col-span-2">
              {/* Tab Navigation */}
              <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                  <button
                    onClick={() => setActiveTab('guide')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'guide'
                        ? 'border-yellow-500 text-yellow-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Guide
                  </button>
                  <button
                    onClick={() => setActiveTab('docs')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'docs'
                        ? 'border-yellow-500 text-yellow-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Docs & Media
                  </button>
                  <button
                    onClick={() => setActiveTab('history')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'history'
                        ? 'border-yellow-500 text-yellow-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    History
                  </button>
                </nav>
              </div>

              {/* Tab Content */}
              <div className="min-h-[400px]">
                {activeTab === 'guide' && (
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">{guide.title}</h3>
                    <div className="prose max-w-none">
                      {renderContent(guide.content)}
                    </div>
                    
                    {/* Inline Images */}
                    {guide.images && guide.images.length > 1 && (
                      <div className="mt-6">
                        <h4 className="text-lg font-medium text-gray-900 mb-3">Images</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {guide.images.slice(1).map((image, index) => renderImage(image, index))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'docs' && (
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Documentation & Media</h3>
                    <div className="space-y-4">
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <h4 className="font-medium text-blue-900 mb-2">Quick Start Video</h4>
                        <p className="text-blue-700 text-sm">
                          Watch a 60-second introduction to this equipment.
                        </p>
                        <button className="mt-2 px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600">
                          Watch Video
                        </button>
                      </div>

                      <div className="p-4 bg-green-50 rounded-lg">
                        <h4 className="font-medium text-green-900 mb-2">Manual & Documentation</h4>
                        <p className="text-green-700 text-sm">
                          Download the full equipment manual and safety documentation.
                        </p>
                        <button className="mt-2 px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600">
                          Download PDF
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'history' && (
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Equipment History</h3>
                    <div className="space-y-4">
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-medium text-gray-900 mb-2">Recent Checkouts</h4>
                        <p className="text-gray-600 text-sm">
                          View the last 5 checkouts and returns for this equipment.
                        </p>
                        <button className="mt-2 px-3 py-1 bg-gray-500 text-white text-sm rounded hover:bg-gray-600">
                          View History
                        </button>
                      </div>
                      
                      <div className="p-4 bg-orange-50 rounded-lg">
                        <h4 className="font-medium text-orange-900 mb-2">Maintenance Log</h4>
                        <p className="text-orange-700 text-sm">
                          Check maintenance history and upcoming service dates.
                        </p>
                        <button className="mt-2 px-3 py-1 bg-orange-500 text-white text-sm rounded hover:bg-orange-600">
                          View Log
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Safety Panel - Always Visible */}
          {(guide.hazards.length > 0 || guide.ppe.length > 0) && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <h3 className="text-lg font-semibold text-red-900 mb-3">Safety Information</h3>
              
              {guide.hazards.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-medium text-red-800 mb-2">Hazards:</h4>
                  <div className="flex flex-wrap gap-2">
                    {guide.hazards.map((hazard, index) => (
                      <span key={index} className="inline-flex items-center px-2 py-1 bg-red-100 text-red-800 text-sm rounded">
                        <span className="mr-1">{getHazardIcon(hazard)}</span>
                        {hazard}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {guide.ppe.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-medium text-red-800 mb-2">Required PPE:</h4>
                  <div className="flex flex-wrap gap-2">
                    {guide.ppe.map((ppe, index) => (
                      <span key={index} className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded">
                        <span className="mr-1">{getPPEIcon(ppe)}</span>
                        {ppe}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="mt-3 p-2 bg-red-100 rounded">
                <p className="text-red-800 text-sm font-medium">
                  Emergency Shut-off: Locate and familiarize yourself with emergency stop procedures before operation.
                </p>
              </div>
            </div>
          )}

          {/* Footer Actions */}
          <div className="mt-6 pt-4 border-t border-gray-200 flex justify-between items-center">
            <div className="flex space-x-4">
              <button
                onClick={reportProblem}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm"
              >
                Report Problem
              </button>
            </div>
            
            <div className="text-sm text-gray-500">
              Last updated: {new Date(guide.updatedAt).toLocaleDateString()}
              {guide.authorName && ` by ${guide.authorName}`}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuideViewer; 